import { useEffect } from "react";
import { CopyInstructionButton } from "./CopyInstructionButton";

function nextStatus(status) {
  if (status === "in-progress") return "completed";
  if (status === "completed") return "todo";
  return "in-progress";
}

function statusLabel(status) {
  if (status === "in-progress") return "Mark complete";
  if (status === "completed") return "Reopen";
  return "Start";
}

function Field({ label, children }) {
  if (!children) return null;
  return (
    <section>
      <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </h3>
      <p className="mt-2 text-sm leading-relaxed text-foreground">{children}</p>
    </section>
  );
}

export function ActionDetailPanel({ action, onClose, onStatusChange, readOnly, site }) {
  useEffect(() => {
    const onKey = (event) => {
      if (event.key === "Escape") onClose();
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  if (!action) return null;

  const minutes = action.time_estimate || action.timeEstimate;
  const results = action.time_to_result || action.timeToResult;
  const meta = [
    action.category,
    minutes ? `${minutes} min` : null,
    action.difficulty,
    results,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <div className="fixed inset-0 z-50">
      <button
        type="button"
        aria-label="Close details"
        className="absolute inset-0 bg-black/40"
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="action-detail-title"
        className="absolute inset-x-0 bottom-0 flex max-h-[88vh] flex-col rounded-t-2xl bg-card shadow-2xl md:inset-y-0 md:left-auto md:right-0 md:h-full md:max-h-none md:w-[28rem] md:rounded-none"
      >
        <div className="sticky top-0 z-10 bg-card">
          <div className="mx-auto mt-2 h-1.5 w-10 rounded-full bg-gray-300 md:hidden" />
          <div className="flex items-start justify-between gap-3 px-5 pb-3 pt-3 md:pt-5">
            <h2
              id="action-detail-title"
              className="text-lg font-semibold leading-snug text-foreground"
            >
              {action.title}
            </h2>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="shrink-0 rounded-full px-2 py-1 text-sm text-muted-foreground hover:bg-gray-100 hover:text-foreground"
            >
              Close
            </button>
          </div>
        </div>

        <div className="space-y-6 overflow-y-auto px-5 pb-8">
          {action.status === "completed" && (
            <p className="text-sm font-medium text-foreground">Done</p>
          )}
          {meta && <p className="text-sm text-muted-foreground">{meta}</p>}
          <Field label="Problem">{action.problem}</Field>
          <Field label="Solution">{action.solution}</Field>
          <Field label="Why it matters">
            {action.business_value || action.businessValue}
          </Field>
          {action.code_example && (
            <section>
              <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Implementation
              </h3>
              <pre className="mt-2 overflow-x-auto rounded-lg bg-gray-900 p-4 text-sm text-gray-100">
                <code>{action.code_example}</code>
              </pre>
            </section>
          )}
          {action.tags?.length > 0 && (
            <p className="text-sm text-muted-foreground">
              {action.tags.join(" · ")}
            </p>
          )}
          <div className="flex flex-wrap items-center gap-3">
            <CopyInstructionButton
              action={action}
              site={site}
              className="rounded-full border border-border px-5 py-2.5 text-sm font-medium text-muted-foreground hover:text-foreground"
            />
            {!readOnly && onStatusChange && (
              <>
                <button
                  type="button"
                  onClick={() => onStatusChange(action.id, nextStatus(action.status))}
                  className="rounded-full bg-blue-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-blue-700"
                >
                  {statusLabel(action.status)}
                </button>
                {action.status !== "completed" && (
                  <button
                    type="button"
                    onClick={() => onStatusChange(action.id, "completed")}
                    className="rounded-full border border-border px-5 py-2.5 text-sm font-medium text-muted-foreground hover:text-foreground"
                  >
                    Done
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
